import { useEffect, useMemo, useState } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import {
  ActionIcon,
  LoadingOverlay,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useMounted } from "@mantine/hooks";
import { IconPaperclip, IconSearch, IconX } from "@tabler/icons-react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { GeneralTabProps } from "@pages/Patients/Laboratory/components/GeneralTab";

import { CustomPagination } from "@components/CustomPagination";

import { formatDateToString } from "@utils/dateValidation";
import { truncateWithEllipsis } from "@utils/truncateText";

import { QP } from "@constants/index";

import { useGetPrescriptionsForPatient } from "@hooks/queries/prescription/useGetPrescriptionsForPatient";
import { useSyncTableQueryParams } from "@hooks/useSyncTableQueryParams";

import { Prescription } from "@customTypes/prescription";

import { TableControls } from "../TableControls";

import { StyledGeneralTab } from "./GeneralTab.styled";


const columnHelper = createColumnHelper<Partial<Prescription>>();

// const data = generateRandomPrescriptions(10);

export const GeneralTab = ({ setIsRefreshing }: GeneralTabProps) => {
  const { t, i18n } = useTranslation();

  const { patientId } = useParams();
  const [searchFilter, setSearchFilter] = useQueryParam(
    QP.SEARCH,
    withDefault(StringParam, "")
  );
  const { debounced, tablePagination, setTablePagination } =
    useSyncTableQueryParams({ searchFilter });
  const [tableData, setTableData] = useState<Partial<Prescription>[] | []>([]);

  const [isMounted, setIsMounted] = useState(true);
  const mounted = useMounted();

  const { data, isError, isSuccess, isLoading, fetchStatus, isRefetching } =
    useGetPrescriptionsForPatient({
      patientId: patientId as string,
      fastConsultationOnly: false,
      pagination: {
        page: tablePagination.page,
        pageSize: tablePagination.pageSize,
      },
      search: debounced.toLocaleUpperCase(),
    });

  const prescriptionColumns = useMemo(
    () => [
      columnHelper.display({
        id: "files",
        cell: info => {
          return info.row.original?.files?.length ? (
            <IconPaperclip color={"var(--mantine-color-primary-6)"} size={16} />
          ) : null;
        },
      }),
      columnHelper.accessor("consultationInfo.cId", {
        header: t("consultation"),
        cell: info => (
          <Stack gap={5}>
            <Text
              fz={14}
              c="accent"
              td={"underline"}
              ff={"var(--font-secondary)"}
              fw={450}>
              <Link
                to={`${info.row.original.id}/medication`}
                state={{ selectedPrescription: info.row.original }}
                className="hover-underline">
                {info.cell.getValue()}
              </Link>
            </Text>
            <Text c="dimmed" fz={13}>
              {formatDateToString(
                info.row.original.consultationInfo?.date as Date
              )}
              {", "}
              {new Date(
                info.row.original.consultationInfo?.date as Date
              ).toLocaleTimeString(i18n.language, {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Text>
          </Stack>
        ),
      }),
      columnHelper.accessor("prescriptionDate", {
        header: t("date-prescribed"),
        cell: info => {
          return (
            <Stack gap={5}>
              <Text fz={14}>
                {formatDateToString(info.cell.getValue() as Date)}
              </Text>
              <Text c="dimmed" fz={13}>
                {(info.cell.getValue() as Date).toLocaleTimeString(
                  i18n.language,
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                )}
              </Text>
            </Stack>
          );
        },
      }),
      columnHelper.accessor("prescribedBy", {
        header: t("prescribed-by"),
        cell: info => truncateWithEllipsis(info.cell.getValue()?.fullName, 15),
      }),
      columnHelper.display({
        id: "action",
        header: isMobile ? t("action") : t("last-updated-action"),
        cell: info => {
          // console.log(info.row.original);
          return <TableControls prescription={info.row.original} />;
        },
      }),
    ],
    [tableData, i18n.language]
  );

  const table = useReactTable({
    data: tableData,
    columns: prescriptionColumns,
    // state: {
    //   globalFilter: searchFilter,
    // },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // globalFilterFn: (
    //   row: Row<Partial<Prescription>>,
    //   columnId: string,
    //   value: string
    // ) => {
    //   if (columnId === "prescribedBy" || columnId === "consultationInfo_cId") {
    //     return filterFns.includesString(row, columnId, value, () => null);
    //   }
    //   return false;
    // },
  });

  useEffect(() => {
    if (data) {
      setTableData(data.newData);
      setTablePagination(data.pagination);
    } else {
      setTableData([]);
    }
  }, [data]);

  useEffect(() => {
    if (setIsRefreshing) setIsRefreshing(isRefetching);
    if (mounted && isRefetching) return;
    if (mounted && !isRefetching) {
      setIsMounted(false);
    }
  }, [isRefetching, mounted]);

  return (
    <>
      <LoadingOverlay
        visible={isRefetching && !!data?.newData?.length && !isMounted}
        zIndex={1000}
        overlayProps={{ blur: 0.1, opacity: 0.6 }}
        loaderProps={{ display: "none" }}
      />
      <div className="filters-wrapper">
        <TextInput
          placeholder={t("search-for-record")}
          w={{ base: 300, md: 350, lg: 400 }}
          value={searchFilter}
          onChange={e =>
            setSearchFilter(
              e.currentTarget.value.toLocaleUpperCase(),
              "replaceIn"
            )
          }
          leftSection={<IconSearch size={16} />}
          rightSection={
            searchFilter && (
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => setSearchFilter(null)}>
                <IconX size={16} />
              </ActionIcon>
            )
          }
        />
      </div>
      <StyledGeneralTab>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        {isSuccess && !data?.newData?.length && debounced ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={prescriptionColumns.length}>
                <p>
                  {t("empty-results-for", {
                    searchFilter: `"${debounced}"`,
                  })}
                </p>
              </td>
            </tr>
          </tbody>
        ) : null}

        {isSuccess && !data?.newData?.length && !debounced ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={prescriptionColumns.length}>
                <p>{t("empty-prescriptions")}</p>
              </td>
            </tr>
          </tbody>
        ) : null}

        {isLoading && fetchStatus === "fetching" && !tableData.length ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={prescriptionColumns.length}>
                <p>{t("loading")}</p>
              </td>
            </tr>
          </tbody>
        ) : null}

        {isError && !data?.newData?.length ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={prescriptionColumns.length}>
                <p>{t("encountered-error")}</p>
              </td>
            </tr>
          </tbody>
        ) : null}

        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr
              key={row.id}
              // onClick={() =>
              //   navigate("medication", {
              //     state: { prescriptionName: row.original.name },
              //   })
              // }
            >
              {row.getVisibleCells().map(cell => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </StyledGeneralTab>

      {(isLoading && fetchStatus === "fetching") || !data ? null : (
        <CustomPagination
          table={table}
          tablePagination={tablePagination}
          otherParams={{
            ...(debounced && { [QP.SEARCH]: debounced }),
            [QP.TAB]: "general",
          }}
        />
      )}
    </>
  );
};
